import { dictionaries } from "@/dictionaries/content";
import type { ISchoolLevel } from "@/providers/store/api/districts/districts.interface";
import type {
  IMyDistrictSchoolListItem,
  ISchoolLevelResponse,
  ISchoolWithDetailsResponse,
} from "@/providers/store/api/schools/schools.interface";
import { SchoolLevelType } from "@/providers/store/api/schools/schools.interface";

const layers = dictionaries.mapPage.layersVisibility.schoolBoundariesLevelLayer;

export const SCHOOL_LEVEL_TYPE_LABELS: Record<SchoolLevelType, string> = {
  [SchoolLevelType.ELEMENTARY]: layers.elementary.label,
  [SchoolLevelType.MIDDLE]: layers.middle.label,
  [SchoolLevelType.HIGH]: layers.high.label,
  [SchoolLevelType.K8]: layers.k8.label,
};

export function isSchoolLevelType(value: string): value is SchoolLevelType {
  return (Object.values(SchoolLevelType) as string[]).includes(value);
}

export function formatSchoolLevelTypeLabel(type: SchoolLevelType | string): string {
  if (isSchoolLevelType(type)) {
    return SCHOOL_LEVEL_TYPE_LABELS[type];
  }

  return type.charAt(0) + type.slice(1).toLowerCase();
}

/** Short names joined with ", " and one trailing ` School` (e.g. K-8 + High → "K-8, High School"). */
export function formatSchoolLevelsJoinedPhrase(shortLabels: string[]): string | null {
  const parts = shortLabels.map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) {
    return null;
  }

  return `${parts.join(", ")} School`;
}

export const SCHOOL_LEVEL_TYPE_ORDER: Record<SchoolLevelType, number> = {
  [SchoolLevelType.K8]: 0,
  [SchoolLevelType.ELEMENTARY]: 1,
  [SchoolLevelType.MIDDLE]: 2,
  [SchoolLevelType.HIGH]: 3,
};

/** K-8 → Elementary → Middle → High (lowest to highest level). */
export function sortSchoolLevelTypeStrings(types: readonly string[]): string[] {
  return [...types].sort((a, b) => {
    const orderA = isSchoolLevelType(a) ? SCHOOL_LEVEL_TYPE_ORDER[a] : Number.POSITIVE_INFINITY;
    const orderB = isSchoolLevelType(b) ? SCHOOL_LEVEL_TYPE_ORDER[b] : Number.POSITIVE_INFINITY;
    return orderA - orderB;
  });
}

function schoolLevelTypesOrderedFromDetails(
  details: ISchoolWithDetailsResponse,
  schoolLevels: ISchoolLevelResponse[] | undefined,
): SchoolLevelType[] {
  if (!schoolLevels?.length || !details.school.schoolLevelIds.length) {
    return [];
  }

  const byId = new Map(schoolLevels.map((l) => [l.id, l.type]));

  return details.school.schoolLevelIds
    .map((id) => byId.get(id))
    .filter((t): t is SchoolLevelType => t != null)
    .sort((a, b) => SCHOOL_LEVEL_TYPE_ORDER[a] - SCHOOL_LEVEL_TYPE_ORDER[b]);
}

/** Map-aligned short names, comma-joined, one trailing ` School` (e.g. "K-8, High School"). */
export function getSchoolLevelsDisplayLabel(
  details: ISchoolWithDetailsResponse,
  schoolLevels: ISchoolLevelResponse[] | undefined,
): string | null {
  const types = schoolLevelTypesOrderedFromDetails(details, schoolLevels);
  if (types.length === 0) {
    return null;
  }

  const shortLabels = types.map((t) => formatSchoolLevelTypeLabel(t));
  return formatSchoolLevelsJoinedPhrase(shortLabels);
}

export interface ISchoolLevelDisplayPair {
  levelId: string;
  type: SchoolLevelType;
  label: string;
}

export function schoolLevelDisplayPairsFromLevelIds(
  levelIds: string[],
  levels: ISchoolLevel[] | undefined,
): ISchoolLevelDisplayPair[] {
  if (!levelIds.length || !levels?.length) {
    return [];
  }

  const byId = new Map(levels.map((l) => [l.id, l]));

  const pairs: ISchoolLevelDisplayPair[] = [];
  for (const id of levelIds) {
    const sl = byId.get(id);
    if (!sl) continue;
    pairs.push({
      levelId: id,
      type: sl.type,
      label: formatSchoolLevelTypeLabel(sl.type),
    });
  }
  return pairs.sort((a, b) => SCHOOL_LEVEL_TYPE_ORDER[a.type] - SCHOOL_LEVEL_TYPE_ORDER[b.type]);
}

export function schoolLevelTypesFromLevelIds(
  levelIds: string[],
  levels: ISchoolLevel[] | undefined,
): SchoolLevelType[] {
  return schoolLevelDisplayPairsFromLevelIds(levelIds, levels).map((p) => p.type);
}

export function schoolTypeLabelsFromLevelIds(
  levelIds: string[],
  levels: ISchoolLevel[] | undefined,
): string[] {
  return schoolLevelDisplayPairsFromLevelIds(levelIds, levels).map((p) => p.label);
}

/**
 * My District table row: pairs from API ids when resolvable; `null` when ids exist but
 * levels are not yet available; one synthetic pair from legacy `level` when present.
 */
export function schoolLevelSegmentsForMyDistrictRow(
  school: IMyDistrictSchoolListItem,
  districtLevels: ISchoolLevel[] | undefined,
): ISchoolLevelDisplayPair[] | null {
  const levelIds = school.schoolLevelIds ?? [];
  const pairs = schoolLevelDisplayPairsFromLevelIds(levelIds, districtLevels);

  if (pairs.length > 0) {
    return pairs;
  }

  if (levelIds.length > 0) {
    return null;
  }

  if (school.level != null) {
    return [
      {
        levelId: `${school.id}-legacy`,
        type: school.level,
        label: formatSchoolLevelTypeLabel(school.level),
      },
    ];
  }

  return null;
}
